import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useCart } from '../Context/CartContext'
import { useCurrency } from '../Context/CurrencyContext'
import useTemplateScripts from '../hooks/useTemplateScripts'
import { getAllProducts, getProductById } from '../services/productService'
import { classifyCategory } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import CartDrawer from './CartDrawer'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import ProductCard from './ProductCard'
import ProductRail from './ProductRail'
import WishlistButton from './WishlistButton'
import './ProductDetail.css'

const RECENT_KEY = 'fabritech-recently-viewed'
const SUGGESTION_LIMIT = 12
const DEFAULT_DESCRIPTION =
  'Professional technology product from Fabritech. Contact us for setup, delivery, and support across Rwanda.'

const TRUST_POINTS = [
  { icon: 'fa-certificate', title: '100% genuine', text: 'Original products only' },
  { icon: 'fa-shield-halved', title: 'Warranty applied', text: 'Manufacturer cover' },
  { icon: 'fa-screwdriver-wrench', title: 'Local support', text: 'Setup & after-sales' },
  { icon: 'fa-mobile-screen', title: 'Easy payment', text: 'MoMo, Airtel Money, card' },
]

const normalizePrice = (price) => {
  if (typeof price === 'number') return price
  if (typeof price === 'string') {
    const numeric = parseFloat(price.replace(/[^\d.]/g, ''))
    return Number.isNaN(numeric) ? 0 : numeric
  }
  return 0
}

const parseDiscount = (discount, price, priceOld) => {
  if (priceOld && priceOld > price) {
    return Math.round(((priceOld - price) / priceOld) * 100)
  }
  if (typeof discount === 'number') return discount
  if (typeof discount === 'string') {
    const parsed = parseFloat(discount.replace(/[%-]/g, ''))
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

const detectBrand = (name = '', category = '') => {
  const haystack = `${name} ${category}`
  if (/starlink/i.test(haystack)) return 'Starlink'
  if (/hikvision/i.test(haystack)) return 'Hikvision'
  if (/tplink|tp-link/i.test(haystack)) return 'TP-Link'
  if (/ubiquiti|unifi/i.test(haystack)) return 'Ubiquiti'
  return null
}

/** Shape ProductCard expects, from an API product or a recently-viewed entry. */
const toCardProduct = (item) => {
  const price = normalizePrice(item.priceNew ?? item.price ?? 0)
  const priceOld = item.priceOld ? normalizePrice(item.priceOld) : null
  return {
    ...item,
    id: item.id || item._id,
    name: item.title || item.name || 'Product',
    price,
    priceOld: priceOld && priceOld > price ? priceOld : null,
    image: item.image || '/placeholder-image.png',
    category: item.category || item.badge || 'general',
    stock: item.stock ?? null,
  }
}

/** "just now", "5 min ago", "3 h ago", "2 days ago" for a recently-viewed timestamp. */
const timeAgo = (time) => {
  const minutes = Math.floor((Date.now() - Number(time)) / 60000)
  if (!(minutes >= 1)) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'yesterday' : `${days} days ago`
}

/** Stock pill: in stock / only N left / out of stock. */
const stockState = (stock, threshold) => {
  if (stock <= 0) return { tone: 'out', label: 'Out of stock' }
  if (stock <= (Number(threshold) || 10)) return { tone: 'low', label: `Only ${stock} left` }
  return { tone: 'ok', label: 'In stock' }
}

function ProductDetail() {
  useTemplateScripts()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { addToCart, cartItems } = useCart()
  const { currency, setCurrency, currencies, formatPrice: formatMoney, isForeign, baseCurrency } = useCurrency()

  const formatPrice = (price) => formatMoney(normalizePrice(price))

  const [quantity, setQuantity] = useState(1)
  const [product, setProduct] = useState(null)
  const [related, setRelated] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [addedToCart, setAddedToCart] = useState(false)
  const [addedCardId, setAddedCardId] = useState(null)
  const [showCart, setShowCart] = useState(false)
  const [toast, setToast] = useState(null)
  const [activeImage, setActiveImage] = useState(0)
  const [zoom, setZoom] = useState(null)
  const [recent, setRecent] = useState([])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 2200)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true)
      setError('')
      setQuantity(1)
      setActiveImage(0)
      window.scrollTo({ top: 0 })

      const stateProduct = location.state?.product
      if (stateProduct) {
        setProduct(stateProduct)
        setLoading(false)
      }

      try {
        const apiProduct = await getProductById(id)
        setProduct(apiProduct)
      } catch (apiError) {
        if (!stateProduct) {
          setError(apiError.message || 'Product not found')
          setProduct(null)
        }
      } finally {
        setLoading(false)
      }
    }

    loadProduct()
  }, [id, location.state])

  useEffect(() => {
    let cancelled = false
    getAllProducts()
      .then((items) => {
        if (!cancelled) setRelated(Array.isArray(items) ? items : [])
      })
      .catch(() => {
        if (!cancelled) setRelated([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const safeProduct = useMemo(() => {
    if (!product) return null
    const price = normalizePrice(product.priceNew ?? product.price ?? 0)
    const priceOld = product.priceOld ? normalizePrice(product.priceOld) : null
    const images = [product.image, ...(product.images || [])]
      .filter(Boolean)
      .filter((src, index, arr) => arr.indexOf(src) === index)
    const name = product.name || product.title || 'Product'
    const category = product.category || product.badge || 'general'
    const brand = product.brand || detectBrand(name, category)
    const stock = Number(product.stock) || 0

    return {
      ...product,
      id: product.id || product._id || id,
      name,
      title: product.title || name,
      category,
      price,
      priceOld: priceOld && priceOld > price ? priceOld : null,
      discount: parseDiscount(product.discount, price, priceOld),
      image: images[0] || '/placeholder-image.png',
      images: images.length ? images : ['/placeholder-image.png'],
      description: product.description || DEFAULT_DESCRIPTION,
      brand,
      stock,
    }
  }, [product, id])

  useEffect(() => {
    if (!safeProduct) return
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
      const next = [
        {
          id: safeProduct.id,
          name: safeProduct.name,
          price: safeProduct.price,
          priceOld: safeProduct.priceOld,
          image: safeProduct.image,
          category: safeProduct.category,
          viewedAt: Date.now(),
        },
        ...stored.filter((item) => String(item.id) !== String(safeProduct.id)),
      ].slice(0, 8)
      localStorage.setItem(RECENT_KEY, JSON.stringify(next))
      setRecent(next.filter((item) => String(item.id) !== String(safeProduct.id)).map(toCardProduct))
    } catch {
      setRecent([])
    }
  }, [safeProduct])

  const productGroup = classifyCategory(safeProduct?.category, safeProduct?.title || safeProduct?.name)

  // Related products only: the same type first (e.g. other CCTV cameras for a CCTV camera), then the
  // rest of the same category (e.g. kits and recorders). Products from other categories never appear.
  const suggestions = useMemo(() => {
    if (!safeProduct) return []
    const own = classifyCategory(safeProduct.category, safeProduct.title || safeProduct.name)
    const others = related.map(toCardProduct).filter((item) => String(item.id) !== String(safeProduct.id))
    const sameType = others.filter((item) => classifyCategory(item.category, item.title || item.name).subcategoryId === own.subcategoryId)
    const sameCategory = others.filter((item) => {
      const group = classifyCategory(item.category, item.title || item.name)
      return group.categoryId === own.categoryId && group.subcategoryId !== own.subcategoryId
    })
    return [...sameType, ...sameCategory].slice(0, SUGGESTION_LIMIT)
  }, [related, safeProduct])

  const clearRecent = () => {
    try {
      // Keep the product on screen so it stays first in the history
      const current = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
        .filter((item) => String(item.id) === String(safeProduct?.id))
      localStorage.setItem(RECENT_KEY, JSON.stringify(current))
    } catch {
      // Storage unavailable: hiding the row is enough
    }
    setRecent([])
  }

  const maxQty = safeProduct ? Math.max(1, safeProduct.stock || 10) : 1
  const soldOut = safeProduct?.stock === 0
  const stock = safeProduct ? stockState(safeProduct.stock, safeProduct.lowStockThreshold) : null
  const savings = safeProduct?.priceOld ? safeProduct.priceOld - safeProduct.price : 0

  const handleAddToCart = async () => {
    if (!safeProduct || soldOut) return false
    try {
      const ok = await addToCart(
        {
          id: safeProduct.id,
          name: safeProduct.name,
          title: safeProduct.title,
          price: safeProduct.price,
          image: safeProduct.image,
          category: safeProduct.category,
        },
        quantity
      )
      if (ok === false) {
        setToast('Could not add to cart. Please try again.')
        return false
      }
      setAddedToCart(true)
      setToast(quantity > 1 ? `${quantity} × ${safeProduct.name} added to cart` : `${safeProduct.name} added to cart`)
      setTimeout(() => setAddedToCart(false), 2200)
      return true
    } catch (err) {
      console.error('Add to cart failed:', err)
      setToast('Could not add to cart. Please try again.')
      return false
    }
  }

  const handleBuyNow = async () => {
    const ok = await handleAddToCart()
    if (ok) navigate('/checkout')
  }

  const shareProduct = async () => {
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({ title: safeProduct?.name, url })
        return
      }
      await navigator.clipboard.writeText(url)
      setToast('Product link copied')
    } catch {
      setToast('Could not share this product')
    }
  }

  const onZoomMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setZoom(`${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`)
  }

  const onCardAdded = (item) => {
    setAddedCardId(item.id)
    setToast(`${item.name} added to cart`)
    setTimeout(() => setAddedCardId(null), 2000)
  }

  const renderCards = (items) =>
    items.map((item) => (
      <li key={item.id}>
        <ProductCard product={item} addedProduct={addedCardId} onAdded={onCardAdded} />
      </li>
    ))

  const shopLink = (categoryId) => `${SITE_LINKS.shop}?category=${categoryId}`

  return (
    <div className="pd-page">
      <MainHeader cartCount={cartItems.length} onCartClick={() => setShowCart((open) => !open)} />

      {loading ? (
        <div className="pd-container pd-state">
          <div className="pd-spinner" />
          <p>Loading product details…</p>
        </div>
      ) : !safeProduct ? (
        <div className="pd-container pd-state">
          <i className="fa-solid fa-box-open" aria-hidden="true" />
          <h2>Product not found</h2>
          <p>{error || "Sorry, this product doesn't exist."}</p>
          <Link to={SITE_LINKS.shop} className="pd-btn pd-btn--primary">
            Back to Shop
          </Link>
        </div>
      ) : (
        <main className="pd-container pd-main">
          <nav className="pd-breadcrumb" aria-label="Breadcrumb">
            <Link to={SITE_LINKS.home}>Home</Link>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            <Link to={SITE_LINKS.shop}>Shop</Link>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            <Link to={shopLink(productGroup.categoryId)}>{productGroup.categoryName}</Link>
            {productGroup.categoryId !== productGroup.subcategoryId && (
              <>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <Link to={shopLink(productGroup.subcategoryId)}>{productGroup.subcategoryName}</Link>
              </>
            )}
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            <span aria-current="page">{safeProduct.name}</span>
          </nav>

          <div className="pd-hero">
            <div className="pd-gallery">
              <div
                className={`pd-stage${zoom ? ' is-zooming' : ''}`}
                onMouseMove={onZoomMove}
                onMouseLeave={() => setZoom(null)}
              >
                {safeProduct.discount ? <span className="pd-sale">-{safeProduct.discount}%</span> : null}
                <img
                  src={safeProduct.images[activeImage]}
                  alt={safeProduct.name}
                  style={zoom ? { transformOrigin: zoom } : undefined}
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder-image.png'
                  }}
                />
                <span className="pd-stage__hint" aria-hidden="true">
                  <i className="fa-solid fa-magnifying-glass-plus" />
                  Hover to zoom
                </span>
              </div>
              {safeProduct.images.length > 1 && (
                <div className="pd-thumbs">
                  {safeProduct.images.map((src, index) => (
                    <button
                      type="button"
                      key={src}
                      className={index === activeImage ? 'is-active' : ''}
                      onClick={() => setActiveImage(index)}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img src={src} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="pd-info">
              <div className="pd-tags">
                {safeProduct.brand && <span className="pd-tag pd-tag--brand">{safeProduct.brand}</span>}
                <Link to={shopLink(productGroup.subcategoryId)} className="pd-tag">
                  {productGroup.subcategoryName}
                </Link>
                <span className={`pd-stock is-${stock.tone}`}>{stock.label}</span>
              </div>

              <h1 className="pd-title">{safeProduct.name}</h1>

              <div className="pd-price">
                <div className="pd-price__row">
                  <strong>{formatPrice(safeProduct.price)}</strong>
                  {safeProduct.priceOld && <s>{formatPrice(safeProduct.priceOld)}</s>}
                  <label className="pd-currency">
                    <span className="pd-visually-hidden">Display currency</span>
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {savings > 0 && (
                  <span className="pd-save">
                    You save {formatPrice(savings)} ({safeProduct.discount}%)
                  </span>
                )}
                {isForeign && (
                  <p className="pd-note">
                    Shown in {currency} for reference. Payment is in {baseCurrency}.
                  </p>
                )}
              </div>

              <div className="pd-buy">
                <div className="pd-qty" aria-label="Quantity">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || soldOut}
                    aria-label="Decrease quantity"
                  >
                    <i className="fa-solid fa-minus" aria-hidden="true" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={quantity}
                    disabled={soldOut}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '')
                      setQuantity(Math.max(1, Math.min(parseInt(digits || '1', 10), maxQty)))
                    }}
                    aria-label="Quantity"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(q + 1, maxQty))}
                    disabled={quantity >= maxQty || soldOut}
                    aria-label="Increase quantity"
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                  </button>
                </div>
                <button
                  type="button"
                  className={`pd-add${addedToCart ? ' is-added' : ''}`}
                  onClick={handleAddToCart}
                  disabled={soldOut}
                >
                  <i className={`fa-solid ${addedToCart ? 'fa-check' : 'fa-cart-shopping'}`} aria-hidden="true" />
                  {soldOut ? 'Out of stock' : addedToCart ? 'Added to cart' : 'Add to Cart'}
                </button>
                <WishlistButton product={safeProduct} className="pd-icon-btn" />
                <button type="button" className="pd-icon-btn" onClick={shareProduct} aria-label="Share product">
                  <i className="fa-solid fa-share-nodes" aria-hidden="true" />
                </button>
              </div>

              <button type="button" className="pd-buy-now" onClick={handleBuyNow} disabled={soldOut}>
                Buy it now
              </button>

              <div className="pd-delivery">
                <i className="fa-solid fa-truck-fast" aria-hidden="true" />
                <p>
                  Delivery in <strong>1–2 business days</strong> across Rwanda. Pay with MTN MoMo, Airtel Money, or bank
                  transfer — orders are confirmed after payment verification.
                </p>
              </div>

              <ul className="pd-trust">
                {TRUST_POINTS.map((point) => (
                  <li key={point.title}>
                    <i className={`fa-solid ${point.icon}`} aria-hidden="true" />
                    <span>
                      <strong>{point.title}</strong>
                      {point.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <section className="pd-overview" aria-labelledby="pd-overview-title">
            <div className="pd-overview__main">
              <span className="fab-chip">Overview</span>
              <h2 id="pd-overview-title">About this product</h2>
              <p className="pd-overview__text">{safeProduct.description}</p>
              <a href={CONTACT_INFO.phoneHref} className="pd-help">
                <span className="pd-help__icon">
                  <i className="fa-solid fa-headset" aria-hidden="true" />
                </span>
                <span>
                  Questions about this product?
                  <strong>Call {CONTACT_INFO.phone}</strong>
                </span>
              </a>
            </div>
          </section>

          {suggestions.length > 0 && (
            <ProductRail
              id="pd-suggestions"
              className="pd-rail"
              icon="fa-layer-group"
              eyebrow="Related products"
              title="You may also like"
              subtitle={`More ${productGroup.subcategoryName} and related ${productGroup.categoryName} products`}
              actions={
                <Link to={shopLink(productGroup.subcategoryId)} className="pd-rail__link">
                  View all {productGroup.subcategoryName}
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
              }
            >
              {renderCards(suggestions)}
            </ProductRail>
          )}

          {recent.length > 0 && (
            <ProductRail
              id="pd-recent"
              className="pd-rail pd-rail--recent"
              icon="fa-clock-rotate-left"
              eyebrow="Your browsing history"
              title="Recently viewed"
              subtitle="Pick up where you left off"
              actions={
                <button type="button" className="pd-rail__clear" onClick={clearRecent}>
                  <i className="fa-solid fa-trash-can" aria-hidden="true" />
                  Clear history
                </button>
              }
            >
              {recent.map((item) => {
                const group = classifyCategory(item.category, item.name)
                return (
                  <li key={item.id}>
                    <Link to={`/product-details/${item.id}`} className="pd-recent">
                      <span className="pd-recent__thumb">
                        <img
                          src={item.image}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder-image.png'
                          }}
                        />
                      </span>
                      <span className="pd-recent__body">
                        <span className="pd-recent__type">{group.subcategoryName}</span>
                        <span className="pd-recent__name" title={item.name}>{item.name}</span>
                        <span className="pd-recent__price">{formatPrice(item.price)}</span>
                        {item.viewedAt && <span className="pd-recent__when">Viewed {timeAgo(item.viewedAt)}</span>}
                      </span>
                      <i className="fa-solid fa-chevron-right pd-recent__go" aria-hidden="true" />
                    </Link>
                  </li>
                )
              })}
            </ProductRail>
          )}
        </main>
      )}

      <HomeFooter />

      <CartDrawer open={showCart} onClose={() => setShowCart(false)} onToast={(message) => setToast(message)} />

      {toast && (
        <div className="pd-toast" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          {toast}
        </div>
      )}
    </div>
  )
}

export default ProductDetail
